// @flow
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { maxMediaQueries, minMediaQueries } from '@finect/tabular-utils/mediaQueries';
import { DatalayerScene } from '@finect/front-gtm';
import { Container } from '@finect/tabular-core/Container';
import { ContainerInner } from '@finect/tabular-core/ContainerInner';
import { Head } from '@finect/tabular-components/Head';
import { Link } from 'react-router-dom';
import { getFont } from '@finect/tabular-utils/getFont';
import VersionNotesModal from '../VersionNotesModal/VersionNotesModal';
import { PlusHero } from '../PlusHero';
import { fetchSetting } from '../../../store/settings/actions';
import { useVersionNotes, PLUS_VERSION_NOTES_SETTING } from '../../../scenes/Plus/VersionNotes/utils';
import { MainContentStyled, FullHeightContainer } from './partials';

const VersionIndicator = styled(Link)`
  display: block;
  width: 95%;
  margin: 40px auto 0;
  text-align: right;
  ${getFont(-2)}
  color: rgba(0, 0, 0, 0.3);
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s;

  &:hover {
    color: ${({ theme }) => theme.plus.background};
  }

  ${maxMediaQueries.phone`
    width: 100%;
    padding: 0 15px;
  `}
`;

const CustomContainer = styled(Container)`
  padding: ${({ $noPadding }) => ($noPadding ? '0' : '30px 0')};
  display: flex;
  flex-direction: column;
  ${({ $noPadding }) => ($noPadding ? 'min-height: 0; flex: 1;' : 'min-height: 400px;')}

  /* El primer hijo (ContainerInner) debe crecer para empujar el resto */
  > div:first-child {
    flex-grow: 1;
  }

  ${({ limited, narrow }) =>
    (limited
      ? `
  > div {
    max-width: 1000px !important;

    > div {
      max-width: 100%;
    }
  }
  `
      : narrow
        ? `
      > div {
        max-width: 1200px !important;

        > div {
          max-width: 100%;
        }
      }
  `
        : '')}

  ${minMediaQueries.phablet`
    padding: ${({ $noPadding }) => ($noPadding ? '0' : '30px 15px')};
    ${({ $noPadding }) => ($noPadding ? '' : 'min-height: calc(100vh - 150px);')}
  `}
`;

type Props = {
  user?: Object,
  section: string,
  children: any,
  title?: string,
  description?: string,
  // Defaults to 'noindex' so Plus pages stay out of the index unless a page opts in
  // (e.g. the public /plus/carteras landing passes robots="index, follow").
  robots?: string,
  // Self-referencing canonical for indexable pages. Only the public landing needs it;
  // noindex pages can omit it (Head renders no <link> when canonical is null).
  canonical?: string,
  withHello?: boolean,
  selectedTab?: {
    id: string,
    metaTitle: string
  },
  sections?: Array<{
    id: string,
    tab: string,
    metaTitle?: string,
    to?: string
  }>,
  // Analytics page-view identity. Default to the section-derived values so existing pages
  // are unchanged; pass distinct values to differentiate pages that share a `section`
  // (e.g. the public /plus/carteras landing vs the user's own /plus/micartera). Pass
  // pageType={null} to suppress the scene entirely (e.g. while a page is still loading).
  pageType?: ?string,
  pageId?: ?string,
  limited?: boolean,
  narrow?: boolean,
  noPadding?: boolean
};

export const PlusContainer = (props: Props) => {
  const {
    title = 'Finect Plus',
    description,
    robots = 'noindex',
    canonical,
    user,
    children,
    section,
    withHello,
    selectedTab,
    sections,
    pageType = `plus - ${section}`,
    pageId = selectedTab?.id || section,
    limited,
    narrow,
    noPadding
  } = props;
  const dispatch = useDispatch();
  const { latestVersion, fetched, fetching } = useVersionNotes();

  useEffect(() => {
    if (user?.plus && !fetched && !fetching) {
      dispatch(fetchSetting(PLUS_VERSION_NOTES_SETTING));
    }
  }, [dispatch, user?.plus, fetched, fetching]);

  return (
    <>
      <Head title={title} description={description} withoutName robots={robots} canonical={canonical} />
      {pageType !== null && <DatalayerScene pageType={pageType} pageId={pageId} />}
      <PlusHero
        user={user}
        section={section}
        activeTab={section}
        withHello={withHello}
        sections={sections}
        limited={limited}
        narrow={narrow}
      />
      {!limited && <VersionNotesModal />}

      <CustomContainer fullWidth limited={limited} narrow={narrow} $noPadding={noPadding}>
        {noPadding ? (
          <FullHeightContainer>{children}</FullHeightContainer>
        ) : (
          <>
            <ContainerInner style={{ maxWidth: '100%' }}>
              <MainContentStyled width="95%">{children}</MainContentStyled>
            </ContainerInner>

            {!limited && !narrow && latestVersion?.version && (
              <VersionIndicator to="/plus/novedades">Plus v{latestVersion.version}</VersionIndicator>
            )}
          </>
        )}
      </CustomContainer>
    </>
  );
};
